import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import Loading from "../../components/common/Loading";
import { Button, Col, Form, FormControl, FormLabel, FormSelect, Row } from "react-bootstrap";
import { ContentService } from "../../../services/ContentService";
import { ILanguage, ISiteMetaData } from "../../../models/Interfaces";
import { MetaDataService } from "../../../services/MetaDataService";
import { CellProps, Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useConfirm } from "../../../config/ConfirmProvider";
import FilteredTable from "../../components/common/FilteredTable";
import AppModal from "../../components/common/AppModal";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./siteMetaData.module.scss";

const SiteMetaData = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<ILanguage | null>(null);
  const [siteMeta, setSiteMeta] = useState<Array<ISiteMetaData>>([]);
  const [selectedMeta, setSelectedMeta] = useState<ISiteMetaData | null>(null);
  const [showDetail, setShowDetail] = useState<boolean>(false);

  const contentSvcRef = useRef<ContentService | null>(null);
  const metaSvrRef = useRef<MetaDataService | null>(null);
  const metaFormRef = useRef<HTMLFormElement | null>(null);
  const metaFvRef = useRef<Core | null>(null);
  
  const confirmation = useConfirm();

  const columns: Column<ISiteMetaData>[] = useMemo(() => [
    {
      Header: "Language",
      id: "language",
      Cell: ({ row }: CellProps<ISiteMetaData>) => {
        return <span>{getLanguageName(row.original.Language)}</span>
      }
    },
    {
      Header: "Meta Key",
      accessor: "MetaKey"
    },
    {
      Header: "Meta Value",
      accessor: "MetaValue"
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<ISiteMetaData>) => (
        <div className="user-result-actions user-result-actions--single">
          <Button variant="light" onClick={() => editSiteMeta(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit meta data">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteSiteMeta(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete meta data">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [selectedLanguage, siteMeta]);

  const createValidator = () : void => {
    if (!metaFvRef.current && metaFormRef.current) {
      metaFvRef.current = createFormValidation(metaFormRef.current, {
        fields: {
          MetaKey: {
            validators: {
              notEmpty: { message: "A key is required for SEO meta data" }
            }
          },
          MetaValue: {
            validators: {
              notEmpty: { message: "Meta data value is required" }
            }
          }
        },
        plugins: {
          trigger: new Trigger(),
          bootstrap5: new Bootstrap5(),          
        }
      });
    }
  };

  const getLanguages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.getSiteLanguages();

      if (res.length > 0) {
        setLanguages(res);
      }
    }
  };

  const getMetaForLanguage = async (): Promise<void> => {
    if (metaSvrRef.current && selectedLanguage) {
      const res = await metaSvrRef.current.getSiteMetaData(selectedLanguage.ShortCode);

      if (res) {
        setSiteMeta(res);
      }
    }
  };

  const getLanguageName = (shortcode: string): string => {
    if (languages) {
      const lang = languages.find(l => l.ShortCode.toLowerCase() === shortcode.toLowerCase());
      return lang?.Language ?? shortcode;
    }
    return shortcode;
  };

  const handleLanguageSelect = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    if (languages.length > 0) {
      const value = event.target.value;

      if (value) {
        const lang = languages.find(l => l.ShortCode.toLowerCase() === value.toLowerCase());
        if (lang) {
          setSelectedLanguage(lang);
        }
      }
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedMeta, (item) => item.Id, () => closeDetail());

  const deleteSiteMeta = async (meta: ISiteMetaData): Promise<void> => {
    if (meta && metaSvrRef.current) {
      const confirmed = await confirmation.requestConfirmation(`Delete <b>${meta.MetaKey}</b> meta data item?<br /><b>This action cannot be undone.</b>`, `Delete ${meta.MetaKey}`);

      if (confirmed) {
        const res = await metaSvrRef.current.deleteSiteMetaData(meta.Id);
        if (res) {
          setSiteMeta(prev => prev.filter(md => md.Id !== meta.Id));
          closeDeletedSelection(meta.Id);
        }
      }
    }
  };

  const editSiteMeta = (meta: ISiteMetaData): void => {
    if (siteMeta) {
      setSelectedMeta(meta);
      setShowDetail(true);
    }
  };

  const addSiteMeta = (): void => {
    if (metaSvrRef.current && selectedLanguage) {
      const newmeta = metaSvrRef.current.newSiteMetadata(selectedLanguage.ShortCode);
      if (newmeta) {
        setSelectedMeta(newmeta);
        setShowDetail(true);
      }
    }
  };

  const saveSiteMeta = async () : Promise<void> => {
    if (metaSvrRef.current && selectedMeta && metaFvRef.current) {
      const validres = await metaFvRef.current.validate();

      if (validres === "Valid") {
        const saveres = await metaSvrRef.current.saveSiteMetaData(selectedMeta);
        if (saveres) {
          const existing = siteMeta.find(sm => sm.Id === saveres.Id);

          if (existing) {
            setSiteMeta(prev => prev.map(sm => sm.Id === saveres.Id ? saveres : sm));
          } else {
            setSiteMeta(prev => [
              ...prev,
              saveres
            ]);
          }
        }
        closeDetail();
      }
    }
  };

  const closeDetail = (): void => {
    setSelectedMeta(null);
    setShowDetail(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedMeta) {
      const { name, value } = event.target;
      setSelectedMeta(prev => ({
        ...prev as ISiteMetaData,
        [name]: value
      }));
    }
  };

  useEffect(() => {
    if (metaFormRef.current) {
      createValidator();
    } else {
      metaFvRef.current = null;
    }
  }, [metaFormRef, showDetail]);

  useEffect(() => {
    if (selectedLanguage) {
      getMetaForLanguage();
    }
  }, [selectedLanguage])

  useEffect(() => {
    const getData = async () => {
      try {
        await getLanguages();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    if (!metaSvrRef.current) {
      metaSvrRef.current = new MetaDataService();
    }

    getData();
  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading site meta data..." SpinSize="5x" />
      )}

      {!loading && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div>
                <h2 className="app-list-section__title">SEO Metadata</h2>
                <p className="app-list-section__summary">
                  {selectedLanguage
                    ? `Search, review, and maintain site metadata for ${selectedLanguage.Language}.`
                    : "Choose a site language to review and maintain its site-level SEO metadata."}
                </p>
              </div>
              <div className={styles.selectorInline}>
                <FormLabel htmlFor="SiteLanguage">Site language</FormLabel>
                <FormSelect
                  id="SiteLanguage"
                  name="SiteLanguage"
                  title="select site language"
                  value={selectedLanguage?.ShortCode ?? ""}
                  onChange={handleLanguageSelect}>
                  <option key="lang-default" value="" disabled>Select site language...</option>
                  {languages.map(l => (
                    <option key={`lang-${l.ShortCode}`} value={l.ShortCode}>{l.Language}</option>
                  ))}
                </FormSelect>
              </div>
            </div>
            <div className="app-list-section__body">
              {selectedLanguage ? (
                <FilteredTable<ISiteMetaData>
                  columns={columns}
                  data={siteMeta}
                  noDataMessage="No SEO meta data found for this site language, click the add button above to add metadata."
                  showAddButton={true}
                  addButtonText="Add site meta data"
                  addButtonHandler={addSiteMeta}
                  filterPlaceHolder="filter site metadata..."
                />
              ) : (
                <div className={styles.emptyState}>
                  <AppEmptyState
                    title="No language selected"
                    message="Choose a site language to view and manage metadata for that language."
                    compact={true}
                  />
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {showDetail && selectedMeta && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Site Meta Data"
          onDataAction={() => saveSiteMeta()}>
          <Form id="MetaDataForm" ref={metaFormRef}>
            <Row>
              <Col xs={12}>
                <FormLabel>Meta Key</FormLabel>
                <FormControl
                  type="text"
                  id="MetaKey"
                  name="MetaKey"
                  value={selectedMeta.MetaKey}
                  placeholder="add a meta data key..."
                  onChange={handleInputChange}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="mt-3">
                <FormLabel>Meta Value</FormLabel>
                <FormControl
                  type="text"
                  id="MetaValue"
                  name="MetaValue"
                  value={selectedMeta.MetaValue}
                  placeholder="add meta data value..."
                  onChange={handleInputChange}
                />
              </Col>
            </Row>
          </Form>
        </AppModal>
      )}
    </>
  );
}

export default SiteMetaData;
